/** Lead completion-proof review — used in Admin Leads tab + Operations Proof Review tab. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle2, XCircle, Eye, RefreshCw, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TONE = {
  pending: "bg-amber-100 text-amber-800",
  verified: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

const fmtAmt = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export function ProofAiSummary({ ai, onRetry }) {
  if (!ai) return null;
  if (ai.status === "pending") {
    return (
      <div className="w-full flex items-center gap-1.5 text-[11px] text-stone-500 mt-1" data-testid="proof-ai-pending">
        <Loader2 className="w-3 h-3 animate-spin" /> AI reading document…
      </div>
    );
  }
  if (ai.status === "failed") {
    return (
      <div className="w-full flex items-center gap-2 text-[11px] text-stone-500 mt-1" data-testid="proof-ai-failed">
        <AlertTriangle className="w-3 h-3 text-amber-600" /> AI could not read this document
        {onRetry && <button onClick={onRetry} className="font-bold text-emerald-800 underline underline-offset-2">Retry</button>}
      </div>
    );
  }
  const f = ai.fields || {};
  return (
    <div className="w-full flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="proof-ai-summary">
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-violet-700"><Sparkles className="w-3 h-3" /> AI</span>
      {(ai.checks || []).map((c) => (
        <span key={c.key} title={c.detail} data-testid={`proof-ai-check-${c.key}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${c.ok ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
          {c.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {c.label}
        </span>
      ))}
      {f.amount > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-stone-100 text-stone-700" title={f.gross_amount > 0 ? `Net of GST · gross paid ${fmtAmt(f.gross_amount)} incl. GST ${fmtAmt(f.gst_amount)}` : "Net premium / basis (excl. GST)"} data-testid="proof-ai-amount">{fmtAmt(f.amount)} <span className="font-normal text-stone-500">excl. GST</span></span>}
      {f.gross_amount > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-stone-50 text-stone-500 border border-stone-200" data-testid="proof-ai-gross">Paid {fmtAmt(f.gross_amount)} incl. GST</span>}
      {f.reference_number && <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-stone-100 text-stone-700">{f.reference_number}</span>}
      {f.document_kind && <span className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-500 capitalize">{f.document_kind}</span>}
      {f.doc_date && <span className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-500">{f.doc_date}</span>}
      {f.notes && <span className="text-[10px] text-amber-700 italic">⚠ {f.notes}</span>}
    </div>
  );
}

export const retryProofAi = async (leadId, proofId) => {
  try {
    await api.post(`/operations/leads/${leadId}/proofs/${proofId}/re-extract`);
    toast.success("AI re-reading the document…");
    return true;
  } catch { toast.error("Retry failed"); return false; }
};

const viewProof = async (leadId, p) => {
  try {
    const r = await api.get(`/leads/${leadId}/proofs/${p.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: p.content_type }));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch { toast.error("Could not open proof"); }
};

const reviewProof = async (leadId, proofId, status, unverify = false) => {
  let remark = "";
  if (status === "rejected") {
    remark = window.prompt(unverify
      ? "Reason for withdrawing this verification (the agent will see this):"
      : "Reason for rejection (the agent will see this):") || "";
    if (!remark.trim()) { toast.error("A reason is required"); return false; }
  }
  try {
    await api.put(`/operations/leads/${leadId}/proofs/${proofId}`, { status, remark });
    toast.success(status === "verified" ? "Proof verified"
      : unverify ? "Verification withdrawn — lead rolled back if it was Approved on this proof"
      : "Proof rejected — agent notified");
    return true;
  } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); return false; }
};

export default function LeadProofsReview({ leadId, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/leads/${leadId}/proofs`).then((r) => setData(r.data)).catch(() => {});
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if ((data?.items || []).some((p) => p.ai?.status === "pending")) {
      const t = setTimeout(load, 4000);
      return () => clearTimeout(t);
    }
  }, [data, load]);

  const act = async (pid, status, unverify = false) => {
    if (await reviewProof(leadId, pid, status, unverify)) { load(); onChanged && onChanged(); }
  };

  const items = data?.items || [];
  return (
    <div className="space-y-2" data-testid={`lead-proofs-review-${leadId}`}>
      <div className="text-xs font-black uppercase text-stone-500">Completion proofs {items.length === 0 && <span className="font-normal normal-case italic text-stone-400">— none uploaded yet (required before Approve)</span>}</div>
      {items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-2.5 bg-white border border-stone-200 rounded-lg px-3 py-2 text-xs" data-testid={`review-proof-row-${p.id}`}>
          <FileText className="w-3.5 h-3.5 text-stone-400" />
          <span className="font-bold">{p.label}</span>
          <span className="text-stone-500 truncate max-w-[180px]">{p.filename}</span>
          <span className={`px-2 py-0.5 rounded-full font-bold ${TONE[p.status]}`}>{p.status}</span>
          {p.remark && <span className="text-stone-500 italic">— {p.remark}</span>}
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => viewProof(leadId, p)} className="inline-flex items-center gap-1 font-bold text-emerald-800 hover:text-emerald-950" data-testid={`review-proof-view-${p.id}`}>
              <Eye className="w-3.5 h-3.5" /> View
            </button>
            {p.status === "pending" && (
              <>
                <Button size="sm" onClick={() => act(p.id, "verified")} data-testid={`review-proof-verify-${p.id}`}
                  className="h-7 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white text-[11px] font-bold px-3">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Verify
                </Button>
                <Button size="sm" variant="outline" onClick={() => act(p.id, "rejected")} data-testid={`review-proof-reject-${p.id}`}
                  className="h-7 rounded-full border-red-300 text-red-700 text-[11px] font-bold px-3">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              </>
            )}
            {p.status === "verified" && (
              <Button size="sm" variant="outline" onClick={() => act(p.id, "rejected", true)} data-testid={`review-proof-unverify-${p.id}`}
                title="Verified by mistake? Withdraw the verification — the lead rolls back to Verification if it was approved on this proof."
                className="h-7 rounded-full border-amber-300 text-amber-800 text-[11px] font-bold px-3">
                <XCircle className="w-3 h-3 mr-1" /> Un-verify
              </Button>
            )}
          </div>
          <ProofAiSummary ai={p.ai} onRetry={async () => { if (await retryProofAi(leadId, p.id)) load(); }} />
        </div>
      ))}
    </div>
  );
}

export function OpsProofReviewTab() {
  const [items, setItems] = useState([]);
  const [exOnly, setExOnly] = useState(false);
  const load = useCallback(() => {
    api.get("/operations/lead-proofs/pending").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (items.some((p) => p.ai?.status === "pending")) {
      const t = setTimeout(load, 4000);
      return () => clearTimeout(t);
    }
  }, [items, load]);

  const act = async (p, status) => {
    if (await reviewProof(p.lead_id, p.id, status)) load();
  };

  const isClean = (p) => p.ai?.status === "success" && (p.ai.checks || []).length > 0 &&
    (p.ai.checks || []).every((c) => c.ok) && !(p.ai.fields?.notes);
  const clean = items.filter(isClean);
  const exceptions = items.filter((p) => !isClean(p));
  const shown = exOnly ? exceptions : [...exceptions, ...clean];

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto" data-testid="ops-proof-review-panel">
      <div className="flex items-center justify-between px-6 pt-5">
        <div>
          <h3 className="font-display text-lg font-black">Completion Proofs — Pending Review</h3>
          <p className="text-xs text-stone-500">Agents upload premium receipts / policy copies / sanction letters etc. A lead cannot be Approved until a proof is verified.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setExOnly((s) => !s)} data-testid="ops-proof-exceptions-toggle"
            className={`h-8 px-3 rounded-full text-xs font-bold border ${exOnly ? "bg-amber-500 text-white border-amber-500" : "border-stone-300 text-stone-600"}`}>
            ⚠ Exceptions only ({exceptions.length})
          </button>
          <span className="text-xs font-bold text-emerald-800" data-testid="ops-proof-clean-count">✓ {clean.length} AI-clean</span>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
        </div>
      </div>
      <table className="w-full text-sm mt-4">
        <thead>
          <tr className="border-b border-stone-200 text-left text-stone-500 text-xs uppercase tracking-wider">
            <th className="py-3 px-6 font-semibold">Lead</th>
            <th className="py-3 px-6 font-semibold">Agent</th>
            <th className="py-3 px-6 font-semibold">Proof</th>
            <th className="py-3 px-6 font-semibold">Uploaded</th>
            <th className="py-3 px-6 font-semibold text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-stone-500">{exOnly ? "No exceptions — everything is AI-clean." : "No proofs waiting for review."}</td></tr>}
          {shown.map((p) => (
            <tr key={p.id} className={`border-b border-stone-100 last:border-0 ${isClean(p) ? "" : "bg-amber-50/40"}`} data-testid={`ops-proof-row-${p.id}`}>
              <td className="py-3 px-6">
                <div className="font-bold">{p.lead?.customer_name} {p.lead?.ref_number && <span className="text-[10px] font-mono text-emerald-800">{p.lead.ref_number}</span>}</div>
                <div className="text-xs text-stone-500">{p.lead?.product_name} · {p.lead?.status}</div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isClean(p) ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {isClean(p) ? "✓ AI clean" : "⚠ Exception"}
                </span>
              </td>
              <td className="py-3 px-6 text-xs">
                <div className="font-semibold">{p.lead?.partner_name}</div>
                <div className="text-stone-500 font-mono">+91 {p.lead?.partner_mobile}</div>
              </td>
              <td className="py-3 px-6 text-xs">
                <div className="font-bold">{p.label}</div>
                <div className="text-stone-500 truncate max-w-[180px]">{p.filename}</div>
                <ProofAiSummary ai={p.ai} onRetry={async () => { if (await retryProofAi(p.lead_id, p.id)) load(); }} />
              </td>
              <td className="py-3 px-6 text-xs text-stone-500">{new Date(p.uploaded_at).toLocaleString()}</td>
              <td className="py-3 px-6">
                <div className="flex items-center justify-end gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => viewProof(p.lead_id, p)} data-testid={`ops-proof-view-${p.id}`}
                    className="h-8 rounded-full text-xs"><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
                  <Button size="sm" onClick={() => act(p, "verified")} data-testid={`ops-proof-verify-${p.id}`}
                    className="h-8 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verify
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act(p, "rejected")} data-testid={`ops-proof-reject-${p.id}`}
                    className="h-8 rounded-full border-red-300 text-red-700 text-xs font-bold">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
